/**
 * Blog AEO helpers: answer-first intros + FAQPage entities from visible HTML.
 * Used by wispPostCleanup (WISP) and generateBlogPostingSchema / prerender.
 */

/** Slugs that get an explicit answer-first block prepended (text from the post itself). */
export const ANSWER_FIRST_BY_SLUG = {
  "knet-vs-myfatoorah-vs-tap-payments-which-is-right-for-your-kuwait-business-in-2026":
    "The right payment provider for a Kuwait business depends on your specific situation. KNET, MyFatoorah, and Tap Payments are all widely used across Kuwait — this guide compares them so you can choose with confidence.",
  "is-replit-secure-enough-for-kuwait-and-gulf-business-applications-in-2026":
    "It depends on what you are building, what data you are handling, and which Replit plan you are using. Replit has significant security capabilities for many Kuwait business use cases, and also limitations when you handle sensitive data or high-stakes production systems.",
  "ai-consulting-in-kuwait-how-to-choose-the-right-ai-partner":
    "Choosing the right AI consulting partner is the critical decision that determines AI success or failure for Kuwait businesses. A true partner goes beyond basic development — look for delivered systems, integration depth, and business outcomes.",
  "ai-implementation-costs-in-kuwait-what-enterprises-should-expect":
    "AI is not a fixed-cost product; it is a strategic investment. Costs vary with scope, data infrastructure, model work, integration complexity, and SaaS vs custom — plus hidden costs like data preparation, change management, maintenance, and scaling.",
};

function stripTags(html = "") {
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Extract FAQ Q&A pairs from post HTML. Prefers explicit Q1:/strong patterns;
 * falls back to question-form H2/H3 followed by the next paragraph.
 * Answers are taken only from visible on-page text.
 */
export function extractFaqPairs(html = "") {
  const src = String(html || "");
  const pairs = [];
  const seen = new Set();

  const push = (q, a) => {
    const qq = stripTags(q).replace(/^Q\d+:\s*/i, "").trim();
    const aa = stripTags(a).trim();
    if (!qq || !aa || qq.length < 8 || aa.length < 20) return;
    if (!qq.includes("?")) return;
    const key = qq.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    pairs.push({ question: qq, answer: aa });
  };

  // Strong question + answer in same <p>
  for (const m of src.matchAll(
    /<p\b[^>]*>\s*<strong>([^<]*\?[^<]*)<\/strong>\s*([\s\S]*?)<\/p>/gi
  )) {
    push(m[1], m[2]);
  }

  // FAQ section: question-form H2/H3 (short text ending in ?) followed by a <p>
  // Restrict heading to text-only so we do not span across later </h3><p> pairs.
  const faqStart = src.search(/frequently asked questions/i);
  const scope = faqStart >= 0 ? src.slice(faqStart) : src;

  for (const m of scope.matchAll(
    /<h([23])\b[^>]*>\s*([^<]{8,160}\?)\s*<\/h\1>\s*<p\b[^>]*>([\s\S]*?)<\/p>/gi
  )) {
    const heading = stripTags(m[2]);
    if (/frequently asked/i.test(heading)) continue;
    push(heading, m[3]);
  }

  return pairs.slice(0, 12);
}

export function faqEntitiesFromPairs(pairs = []) {
  return pairs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: {
      "@type": "Answer",
      text: answer,
    },
  }));
}

/** Prepend a visible answer-first paragraph when configured for the slug. */
export function prependAnswerFirst(html = "", slug = "") {
  const key = String(slug || "").trim().toLowerCase();
  const text = ANSWER_FIRST_BY_SLUG[key];
  if (!text) return String(html || "");
  const marker = 'data-jg-answer-first="true"';
  if (String(html).includes(marker)) return String(html || "");
  const block = `<p ${marker}><strong>Direct answer:</strong> ${text}</p>`;
  return block + String(html || "");
}

/**
 * Resolve OG/image URL: replace Unsplash (and empty) with a branded static path.
 * Branded files are generated into public/pictures/og/blog/ by scripts/generate-blog-og.py.
 */
export function brandedBlogOgPath(slug) {
  const s = String(slug || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");
  if (!s) return "/og-image.png";
  return `/pictures/og/blog/${s}.png`;
}

export function resolveBlogImage(image, slug) {
  const raw = String(image || "").trim();
  if (!raw || /images\.unsplash\.com/i.test(raw) || /unsplash\.com/i.test(raw)) {
    return brandedBlogOgPath(slug);
  }
  return raw;
}
